import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checkInsApi } from '../check-ins';
import type {
  CreateCheckInDto,
  CheckInQueryParams,
} from '../../model/types/volunteers';

// Query keys
export const checkInKeys = {
  all: ['check-ins'] as const,
  lists: () => [...checkInKeys.all, 'list'] as const,
  list: (recitalId: string, params?: CheckInQueryParams) =>
    [...checkInKeys.lists(), recitalId, params] as const,
  stats: (recitalId: string, showId?: string) => 
    [...checkInKeys.all, 'stats', recitalId, showId] as const,
  studentsAvailable: (recitalId: string, showId?: string) =>
    [...checkInKeys.all, 'students-available', recitalId, showId] as const,
};

// Hooks

// Get check-ins for a recital
export const useCheckIns = (recitalId: string, params?: CheckInQueryParams) => {
  return useQuery({
    queryKey: checkInKeys.list(recitalId, params),
    queryFn: () => checkInsApi.list(recitalId, params),
    enabled: !!recitalId,
  });
};

// Get check-in statistics
export const useCheckInStats = (recitalId: string, showId?: string) => {
  return useQuery({
    queryKey: checkInKeys.stats(recitalId, showId),
    queryFn: () => checkInsApi.getStats(recitalId, showId),
    enabled: !!recitalId,
    refetchInterval: 10000, // Refetch every 10 seconds for real-time stats
  });
};



// Create check-in
export const useCreateCheckIn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recitalId,
      data,
    }: {
      recitalId: string;
      data: CreateCheckInDto;
    }) => checkInsApi.create(recitalId, data),
    onSuccess: (_, variables) => {
      // Invalidate check-in lists and stats
      queryClient.invalidateQueries({
        queryKey: checkInKeys.list(variables.recitalId),
      });
      queryClient.invalidateQueries({
        queryKey: checkInKeys.stats(variables.recitalId),
      });
      queryClient.invalidateQueries({
        queryKey: checkInKeys.studentsAvailable(variables.recitalId),
      });
    },
  });
};

// Check out student or volunteer
export const useCheckOut = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recitalId,
      data,
    }: {
      recitalId: string;
      data: CreateCheckInDto;
    }) => checkInsApi.checkOut(recitalId, data),
    onSuccess: (_, variables) => {
      // Invalidate check-in lists and stats
      queryClient.invalidateQueries({
        queryKey: checkInKeys.list(variables.recitalId),
      });
      queryClient.invalidateQueries({
        queryKey: checkInKeys.stats(variables.recitalId),
      });
      queryClient.invalidateQueries({
        queryKey: checkInKeys.studentsAvailable(variables.recitalId),
      });
    },
  });
};

// Get students available for check-in
export const useStudentsAvailableForCheckIn = (recitalId: string, showId?: string) => {
  return useQuery({
    queryKey: checkInKeys.studentsAvailable(recitalId, showId),
    queryFn: () => checkInsApi.getStudentsAvailableForCheckIn(recitalId, showId),
    enabled: !!recitalId,
    refetchInterval: 10000, // Refetch every 10 seconds for real-time updates
  });
};


